import React from 'react';

import useClassy from '@core/hooks/useClassy';
import useEventListener from '@core/hooks/useEventListener';
import useUniqueId from '@core/hooks/useUniqueId';

import ErrorBoundary from '@ui/ErrorBoundary';
import RouteChangePrompt from '@ui/RouteChangePrompt';

import { useEditorKeyContext, useSuperHubEditorFormContext } from '../Context';

import styles from './index.module.scss';
import useEditorFormOnSubmit from './useEditorFormOnSubmit';

/**
 * Renders the top-level `<form>` element and handles all form submission logic
 * and validation error handling.
 */
const FormRoot: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const bem = useClassy(styles, 'FormRoot');
  const uid = useUniqueId('FormRoot');

  const {
    formState: { isDirty },
    reset,
  } = useSuperHubEditorFormContext();
  const onSubmit = useEditorFormOnSubmit();

  const { refreshEditorKey } = useEditorKeyContext();

  // Enable hotkey to trigger form submission.
  useEventListener('keydown', (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      if (isDirty) {
        onSubmit();
      }
    }
  });

  return (
    <ErrorBoundary className={bem('-error-boundary')}>
      <form className={bem()} id={uid('form')} onSubmit={onSubmit}>
        {children}
        <RouteChangePrompt
          onConfirm={() => {
            // Reset the form to its default values when the user confirms they want to leave. This prevents
            // the user from seeing stale data when they return to the editor.
            reset();
            refreshEditorKey();
          }}
          showWhen={isDirty}
          theme="dark"
        />
      </form>
    </ErrorBoundary>
  );
};

export default FormRoot;
